import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ChevronRight,
  Bot,
  Zap,
  Search,
  ShoppingCart,
  Users,
  MessageSquare,
  FileText,
  TrendingUp,
  AlertCircle,
  CheckCircle,
} from 'lucide-react';
import StaticPageLayout from '../../../components/StaticPageLayout';
import { useThemeStore } from '../../../store/theme';

const sections = [
  { id: 'overview', title: 'Overview' },
  { id: 'what-are-bots', title: 'What Are SweetPilot Bots?' },
  { id: 'bot-categories', title: 'Bot Categories' },
  { id: 'popular-bots', title: 'Popular Bots' },
  { id: 'how-to-access', title: 'How to Access Bots' },
  { id: 'setup-guide', title: 'Setup Guide' },
  { id: 'migration', title: 'Migration to AI Employees' },
  { id: 'faq', title: 'Frequently Asked Questions' },
];

const botCategories = [
  { name: 'Marketing Bots', icon: TrendingUp, count: 15, color: 'purple' },
  { name: 'Sales Bots', icon: ShoppingCart, count: 12, color: 'blue' },
  { name: 'Customer Support', icon: MessageSquare, count: 8, color: 'green' },
  { name: 'Research Bots', icon: Search, count: 10, color: 'orange' },
  { name: 'HR & Recruitment', icon: Users, count: 6, color: 'pink' },
  { name: 'Content Creation', icon: FileText, count: 14, color: 'cyan' },
];

const popularBots = [
  {
    name: 'Find Competitors Bot',
    description: 'Automatically discovers and analyzes your competitors, providing insights into their strategies, pricing, and market positioning.',
    category: 'Research',
  },
  {
    name: 'Dropship Product Finder',
    description: 'Identifies trending products for dropshipping businesses with profit margin analysis and supplier recommendations.',
    category: 'E-commerce',
  },
  {
    name: 'Discord Event Bot',
    description: 'Manages community events, sends reminders, and tracks attendance for Discord servers.',
    category: 'Community',
  },
  {
    name: 'Agency Client Onboarding',
    description: 'Streamlines client onboarding with automated welcome sequences, document collection, and task assignment.',
    category: 'Agency',
  },
  {
    name: 'Lead Qualification Bot',
    description: 'Automatically qualifies leads based on custom criteria, scoring, and routing to appropriate team members.',
    category: 'Sales',
  },
  {
    name: 'Content Calendar Bot',
    description: 'Plans and schedules content across multiple platforms with AI-powered topic suggestions.',
    category: 'Marketing',
  },
];

const faqs = [
  {
    question: 'Are SweetPilot Bots still available?',
    answer: 'SweetPilot Bots are legacy products that will reach end-of-life on February 1st, 2026. We recommend migrating to our AI Employees platform for enhanced automation capabilities.',
  },
  {
    question: 'Can I still use my purchased bots?',
    answer: 'Yes, all purchased bots remain accessible until the EOL date. You can access them through your SweetPilot Dashboard under the legacy section.',
  },
  {
    question: 'How do I migrate from Bots to AI Employees?',
    answer: 'Our AI Employees platform offers similar functionality with more advanced features. Contact our support team for a personalized migration guide.',
  },
  {
    question: 'Will my bot configurations be saved?',
    answer: 'We recommend exporting your bot configurations before the EOL date. Our support team can help you transfer settings to AI Employees.',
  },
];

const relatedArticles = [
  { title: 'SweetPilot Legacy', href: '/doc/article/sweetpilot-legacy' },
  { title: 'SweetPilot Prompts', href: '/doc/article/sweetpilot-prompts' },
  { title: 'What is SweetPilot?', href: '/doc/article/what-is-sweetpilot' },
  { title: 'How to get started with SweetPilot', href: '/doc/article/getting-started' },
];

export default function SweetPilotBots() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const [activeSection, setActiveSection] = useState('overview');

  useEffect(() => {
    const handleScroll = () => {
      const sectionElements = sections.map(section => ({
        id: section.id,
        element: document.getElementById(section.id),
      }));

      const currentSection = sectionElements.find(section => {
        if (section.element) {
          const rect = section.element.getBoundingClientRect();
          return rect.top <= 150 && rect.bottom >= 150;
        }
        return false;
      });

      if (currentSection) {
        setActiveSection(currentSection.id);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  const getColorClasses = (color: string) => {
    const colors: Record<string, { bg: string; border: string; icon: string }> = {
      purple: {
        bg: isDark ? 'bg-purple-500/10' : 'bg-purple-50',
        border: isDark ? 'border-purple-500/20' : 'border-purple-200',
        icon: isDark ? 'text-purple-400' : 'text-purple-600',
      },
      blue: {
        bg: isDark ? 'bg-blue-500/10' : 'bg-blue-50',
        border: isDark ? 'border-blue-500/20' : 'border-blue-200',
        icon: isDark ? 'text-blue-400' : 'text-blue-600',
      },
      green: {
        bg: isDark ? 'bg-green-500/10' : 'bg-green-50',
        border: isDark ? 'border-green-500/20' : 'border-green-200',
        icon: isDark ? 'text-green-400' : 'text-green-600',
      },
      orange: {
        bg: isDark ? 'bg-orange-500/10' : 'bg-orange-50',
        border: isDark ? 'border-orange-500/20' : 'border-orange-200',
        icon: isDark ? 'text-orange-400' : 'text-orange-600',
      },
      pink: {
        bg: isDark ? 'bg-pink-500/10' : 'bg-pink-50',
        border: isDark ? 'border-pink-500/20' : 'border-pink-200',
        icon: isDark ? 'text-pink-400' : 'text-pink-600',
      },
      cyan: {
        bg: isDark ? 'bg-cyan-500/10' : 'bg-cyan-50',
        border: isDark ? 'border-cyan-500/20' : 'border-cyan-200',
        icon: isDark ? 'text-cyan-400' : 'text-cyan-600',
      },
    };
    return colors[color] || colors.purple;
  };

  // SEO Schema markup for structured data
  const schemaMarkup = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'SweetPilot Bots - AI Automation Bots for Business',
    description: 'Comprehensive guide to SweetPilot Bots - automated AI bots for marketing, sales, customer support, and business automation.',
    author: { '@type': 'Organization', name: 'SweetPilot' },
    publisher: {
      '@type': 'Organization',
      name: 'SweetPilot',
      logo: { '@type': 'ImageObject', url: 'https://sweetpilot.com/logo.png' }
    },
    datePublished: '2024-01-01',
    dateModified: new Date().toISOString().split('T')[0],
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://sweetpilot.com/doc/article/sweetpilot-bots'
    }
  };

  return (
    <>
      <Helmet>
        <title>SweetPilot Bots - AI Automation Bots for Business | SweetPilot Help Center</title>
        <meta name="description" content="Discover SweetPilot Bots - powerful AI automation bots for marketing, sales, customer support, research, and content creation. Learn how to set up and use automation bots." />
        <meta name="keywords" content="SweetPilot Bots, AI automation, business automation, marketing bots, sales bots, customer support automation, AI chatbots, workflow automation, SweetPilot Max, automation tools" />
        <meta property="og:title" content="SweetPilot Bots - AI Automation Bots for Business" />
        <meta property="og:description" content="Comprehensive guide to SweetPilot Bots - automated AI bots for marketing, sales, customer support, and business automation." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://sweetpilot.com/doc/article/sweetpilot-bots" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="SweetPilot Bots - AI Automation Bots" />
        <meta name="twitter:description" content="Learn about SweetPilot Bots for business automation, marketing, sales, and customer support." />
        <link rel="canonical" href="https://sweetpilot.com/doc/article/sweetpilot-bots" />
        <script type="application/ld+json">{JSON.stringify(schemaMarkup)}</script>
      </Helmet>

      <StaticPageLayout title="" subtitle="" showBackButton={false}>
        {/* Breadcrumbs */}
        <nav className="mb-8" aria-label="Breadcrumb">
          <ol className={`flex items-center flex-wrap gap-2 text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
            <li>
              <Link to="/docs" className={`transition-colors ${isDark ? 'hover:text-purple-400' : 'hover:text-purple-600'}`}>
                All Collections
              </Link>
            </li>
            <li><ChevronRight className="w-4 h-4" aria-hidden="true" /></li>
            <li>
              <Link to="/doc/collections/getting-started" className={`transition-colors ${isDark ? 'hover:text-purple-400' : 'hover:text-purple-600'}`}>
                Getting started
              </Link>
            </li>
            <li><ChevronRight className="w-4 h-4" aria-hidden="true" /></li>
            <li>
              <Link to="/doc/collections/legacy-products" className={`transition-colors ${isDark ? 'hover:text-purple-400' : 'hover:text-purple-600'}`}>
                SweetPilot AI Legacy products
              </Link>
            </li>
            <li><ChevronRight className="w-4 h-4" aria-hidden="true" /></li>
            <li className={isDark ? 'text-white' : 'text-slate-900'}>
              SweetPilot Bots
            </li>
          </ol>
        </nav>

        <div className="flex gap-10">
          {/* Main Content */}
          <article className="flex-1 max-w-3xl">
            {/* Header */}
            <motion.header
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8"
            >
              <h1 className={`text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                SweetPilot Bots
              </h1>
              <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                Discover the automated AI bots that powered early SweetPilot workflows and learn how to migrate to our modern AI Employees platform.
              </p>
              <div className={`flex items-center gap-3 mt-4 text-sm ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isDark ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
                  <Bot className={`w-4 h-4 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                </div>
                <div>
                  <span>Written by </span>
                  <span className={isDark ? 'text-white' : 'text-slate-900'}>Support Team</span>
                </div>
                <span>•</span>
                <span>Updated recently</span>
              </div>
            </motion.header>

            {/* EOL Notice */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className={`p-5 rounded-xl border mb-8 ${
                isDark ? 'bg-orange-500/10 border-orange-500/30' : 'bg-orange-50 border-orange-200'
              }`}
              role="alert"
            >
              <div className="flex items-start gap-3">
                <AlertCircle className={`w-6 h-6 flex-shrink-0 ${isDark ? 'text-orange-400' : 'text-orange-600'}`} />
                <div>
                  <p className={`font-semibold mb-2 ${isDark ? 'text-orange-300' : 'text-orange-800'}`}>
                    Legacy Product Notice
                  </p>
                  <p className={`text-sm ${isDark ? 'text-orange-200/80' : 'text-orange-700'}`}>
                    SweetPilot Bots are part of our legacy product line (SweetPilot Max). These automation bots will reach end-of-life on <strong>February 1st, 2026</strong>. We recommend migrating to our{' '}
                    <Link
                      to="/dashboard/employees"
                      className={`underline ${isDark ? 'text-orange-300 hover:text-orange-200' : 'text-orange-800 hover:text-orange-900'}`}
                    >
                      AI Employees
                    </Link>
                    {' '}platform for enhanced automation capabilities.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Overview Section */}
            <motion.section
              id="overview"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="mb-10"
            >
              <p className={`leading-relaxed mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                <strong>SweetPilot Bots</strong> were automated AI-powered assistants designed to handle specific business tasks without manual intervention. From finding competitors to managing Discord communities, these bots helped thousands of businesses automate their workflows and save countless hours.
              </p>

              <div className={`p-4 rounded-xl border ${isDark ? 'bg-blue-500/10 border-blue-500/20' : 'bg-blue-50 border-blue-200'}`}>
                <div className="flex items-center gap-3">
                  <Zap className={`w-5 h-5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                  <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
                    <strong className={isDark ? 'text-white' : 'text-slate-900'}>Did you know?</strong> SweetPilot Bots processed over 1 million automated tasks in their first year, helping businesses save an average of 15 hours per week.
                  </p>
                </div>
              </div>
            </motion.section>

            {/* What Are Bots Section */}
            <motion.section
              id="what-are-bots"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mb-10"
            >
              <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                What Are SweetPilot Bots?
              </h2>

              <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                SweetPilot Bots are pre-configured automation workflows that combine AI capabilities with specific business functions. Each bot is designed to accomplish particular tasks such as:
              </p>

              <ul className={`space-y-3 mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                <li className="flex items-start gap-3">
                  <CheckCircle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                  <span><strong>Market Research:</strong> Automatically gather competitor data, market trends, and industry insights</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                  <span><strong>Lead Generation:</strong> Find and qualify potential customers based on your criteria</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                  <span><strong>Customer Support:</strong> Handle common inquiries and route complex issues to team members</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                  <span><strong>Content Management:</strong> Schedule posts, manage calendars, and organize content workflows</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                  <span><strong>E-commerce Operations:</strong> Product research, inventory management, and order processing</span>
                </li>
              </ul>
            </motion.section>

            {/* Bot Categories Section */}
            <motion.section
              id="bot-categories"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
              className="mb-10"
            >
              <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Bot Categories
              </h2>

              <p className={`mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                SweetPilot Bots are organized into six main categories, each designed to address specific business needs:
              </p>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                {botCategories.map((category) => {
                  const colors = getColorClasses(category.color);
                  const Icon = category.icon;
                  return (
                    <div
                      key={category.name}
                      className={`p-4 rounded-xl border text-center ${colors.bg} ${colors.border}`}
                    >
                      <Icon className={`w-8 h-8 mx-auto mb-2 ${colors.icon}`} />
                      <p className={`font-medium text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {category.name}
                      </p>
                      <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
                        {category.count} bots available
                      </p>
                    </div>
                  );
                })}
              </div>
            </motion.section>

            {/* Popular Bots Section */}
            <motion.section
              id="popular-bots"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="mb-10"
            >
              <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Popular Bots
              </h2>

              <p className={`mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                Here are some of the most popular SweetPilot Bots used by our community:
              </p>

              <div className="space-y-4">
                {popularBots.map((bot, index) => (
                  <div
                    key={bot.name}
                    className={`p-4 rounded-xl border ${isDark ? 'bg-dark-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`p-2 rounded-lg ${isDark ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
                        <Bot className={`w-5 h-5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                            {bot.name}
                          </h3>
                          <span className={`px-2 py-0.5 rounded-full text-xs ${isDark ? 'bg-purple-500/20 text-purple-300' : 'bg-purple-100 text-purple-700'}`}>
                            {bot.category}
                          </span>
                        </div>
                        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                          {bot.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.section>

            {/* How to Access Section */}
            <motion.section
              id="how-to-access"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
              className="mb-10"
            >
              <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                How to Access SweetPilot Bots
              </h2>

              <ol className={`list-decimal list-inside space-y-3 mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                <li>
                  Navigate to your{' '}
                  <Link
                    to="/dashboard"
                    className={`underline ${isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'}`}
                  >
                    SweetPilot Dashboard
                  </Link>
                  {' '}and sign in with your credentials.
                </li>
                <li>Look for the <strong>Legacy</strong> section in the sidebar navigation.</li>
                <li>Select <strong>"Bots"</strong> to view all available automation bots.</li>
                <li>Click on any bot card to view details and setup instructions.</li>
              </ol>

              <div className={`p-4 rounded-xl border ${isDark ? 'bg-purple-500/10 border-purple-500/20' : 'bg-purple-50 border-purple-200'}`}>
                <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
                  <strong className={isDark ? 'text-white' : 'text-slate-900'}>Note:</strong> Access to SweetPilot Bots requires an active SweetPilot Max subscription. If you don't see the bots section, contact{' '}
                  <a
                    href="mailto:help@sweetpilot.com"
                    className={`underline ${isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'}`}
                  >
                    help@sweetpilot.com
                  </a>
                  {' '}for assistance.
                </p>
              </div>
            </motion.section>

            {/* Setup Guide Section */}
            <motion.section
              id="setup-guide"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="mb-10"
            >
              <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Bot Setup Guide
              </h2>

              <p className={`mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                Each SweetPilot Bot comes with a comprehensive setup guide. Here's the general process:
              </p>

              <div className="space-y-4 mb-6">
                <div className={`p-4 rounded-xl border ${isDark ? 'bg-dark-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-green-500/20' : 'bg-green-100'}`}>
                      <span className={`font-bold text-sm ${isDark ? 'text-green-400' : 'text-green-600'}`}>1</span>
                    </div>
                    <div>
                      <h4 className={`font-semibold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>Select Your Bot</h4>
                      <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                        Browse the bot library and click "View Bot" on the automation you want to set up.
                      </p>
                    </div>
                  </div>
                </div>

                <div className={`p-4 rounded-xl border ${isDark ? 'bg-dark-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-green-500/20' : 'bg-green-100'}`}>
                      <span className={`font-bold text-sm ${isDark ? 'text-green-400' : 'text-green-600'}`}>2</span>
                    </div>
                    <div>
                      <h4 className={`font-semibold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>Watch the Video Walkthrough</h4>
                      <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                        Each bot includes a video tutorial that guides you through the setup process step-by-step.
                      </p>
                    </div>
                  </div>
                </div>

                <div className={`p-4 rounded-xl border ${isDark ? 'bg-dark-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-green-500/20' : 'bg-green-100'}`}>
                      <span className={`font-bold text-sm ${isDark ? 'text-green-400' : 'text-green-600'}`}>3</span>
                    </div>
                    <div>
                      <h4 className={`font-semibold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>Configure Settings</h4>
                      <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                        Customize the bot parameters, connect required integrations, and set your preferences.
                      </p>
                    </div>
                  </div>
                </div>

                <div className={`p-4 rounded-xl border ${isDark ? 'bg-dark-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-green-500/20' : 'bg-green-100'}`}>
                      <span className={`font-bold text-sm ${isDark ? 'text-green-400' : 'text-green-600'}`}>4</span>
                    </div>
                    <div>
                      <h4 className={`font-semibold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>Activate & Monitor</h4>
                      <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                        Launch the bot and monitor its performance through the dashboard analytics.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.section>

            {/* Migration Section */}
            <motion.section
              id="migration"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45 }}
              className="mb-10"
            >
              <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Migration to AI Employees
              </h2>

              <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                As SweetPilot Bots approach their end-of-life date, we encourage all users to migrate to our modern{' '}
                <Link
                  to="/dashboard/employees"
                  className={`underline ${isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'}`}
                >
                  AI Employees
                </Link>
                {' '}platform. AI Employees offer:
              </p>

              <ul className={`space-y-2 mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                <li className="flex items-start gap-2">
                  <span className={`w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0 ${isDark ? 'bg-green-400' : 'bg-green-600'}`}></span>
                  <span>More advanced AI models including GPT-4, Claude, and custom-trained models</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className={`w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0 ${isDark ? 'bg-green-400' : 'bg-green-600'}`}></span>
                  <span>Better integration with modern tools and platforms</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className={`w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0 ${isDark ? 'bg-green-400' : 'bg-green-600'}`}></span>
                  <span>Customizable workflows with drag-and-drop automation builder</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className={`w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0 ${isDark ? 'bg-green-400' : 'bg-green-600'}`}></span>
                  <span>Enhanced analytics and performance tracking</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className={`w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0 ${isDark ? 'bg-green-400' : 'bg-green-600'}`}></span>
                  <span>Dedicated support for migration assistance</span>
                </li>
              </ul>

              <div className={`p-4 rounded-xl border ${isDark ? 'bg-green-500/10 border-green-500/20' : 'bg-green-50 border-green-200'}`}>
                <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
                  <strong className={isDark ? 'text-white' : 'text-slate-900'}>Ready to migrate?</strong> Contact our support team at{' '}
                  <a
                    href="mailto:help@sweetpilot.com"
                    className={`underline ${isDark ? 'text-green-400 hover:text-green-300' : 'text-green-700 hover:text-green-800'}`}
                  >
                    help@sweetpilot.com
                  </a>
                  {' '}and we'll provide a personalized migration plan for your bots.
                </p>
              </div>
            </motion.section>

            {/* FAQ Section */}
            <motion.section
              id="faq"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="mb-10"
            >
              <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Frequently Asked Questions
              </h2>

              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div
                    key={index}
                    className={`p-4 rounded-xl border ${isDark ? 'bg-dark-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}
                  >
                    <h3 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      {faq.question}
                    </h3>
                    <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                      {faq.answer}
                    </p>
                  </div>
                ))}
              </div>
            </motion.section>

            {/* Divider */}
            <hr className={`my-10 ${isDark ? 'border-white/10' : 'border-slate-200'}`} />

            {/* Related Articles */}
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55 }}
              className="mb-10"
            >
              <h2 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Related Articles
              </h2>
              <div className={`rounded-xl border overflow-hidden ${isDark ? 'bg-dark-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
                {relatedArticles.map((article, index) => (
                  <Link
                    key={article.title}
                    to={article.href}
                    className={`flex items-center justify-between p-4 transition-colors ${
                      index !== relatedArticles.length - 1
                        ? isDark ? 'border-b border-white/5' : 'border-b border-slate-100'
                        : ''
                    } ${isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`}
                  >
                    <span className={isDark ? 'text-white' : 'text-slate-900'}>{article.title}</span>
                    <ChevronRight className={`w-5 h-5 ${isDark ? 'text-gray-600' : 'text-slate-400'}`} />
                  </Link>
                ))}
              </div>
            </motion.section>

            {/* Feedback */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className={`text-center p-8 rounded-xl border ${isDark ? 'bg-dark-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}
            >
              <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                Did this answer your question?
              </p>
              <div className="flex justify-center gap-4">
                <button className="text-3xl hover:scale-110 transition-transform" title="Not helpful">
                  😞
                </button>
                <button className="text-3xl hover:scale-110 transition-transform" title="Somewhat helpful">
                  😐
                </button>
                <button className="text-3xl hover:scale-110 transition-transform" title="Very helpful">
                  😃
                </button>
              </div>
            </motion.div>
          </article>

          {/* Sidebar TOC */}
          <aside className="hidden lg:block w-64 flex-shrink-0" aria-label="Table of contents">
            <div className="sticky top-24">
              <nav className={`border-l ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className={`block w-full text-left px-4 py-2 text-sm transition-colors ${
                      activeSection === section.id
                        ? isDark
                          ? 'text-white border-l-2 border-purple-500 -ml-px bg-purple-500/10'
                          : 'text-purple-600 border-l-2 border-purple-600 -ml-px bg-purple-50'
                        : isDark
                          ? 'text-gray-400 hover:text-white'
                          : 'text-slate-500 hover:text-slate-900'
                    }`}
                  >
                    {section.title}
                  </button>
                ))}
              </nav>
            </div>
          </aside>
        </div>
      </StaticPageLayout>
    </>
  );
}
